import { IonButton } from "@ionic/react";
import { translate } from "components/string-table";
import { Alert, Colors, Icon, Section } from "main";
import { subPhotoPlaceholderPNG } from "swing-assets";
import { phoneNumberFormatter, phoneNumberParser } from "swing-utils";

import styles from "./ContactInfo.module.css";

type EditableContactInfo =
  | {
      isEditable: false;
    }
  | {
      isEditable: true;
      onEditAvatar: () => void;
      onEditPhoneNumber: () => void;
    };

export type ContactInfoProps = {
  email?: string;
  editable?: EditableContactInfo;
  name: string;
  phoneNumber?: string;
  isPhoneNumberVerified?: boolean;
  profileImageUrl?: string;
  // TODO: Now that the Badge has been moved,
  // to swing-components, we should directly
  // import the component instead of passing in as a prop
  badge?: JSX.Element;
  isSwingHero?: boolean;
};

export function ContactInfo(props: ContactInfoProps) {
  const {
    email,
    editable,
    name,
    phoneNumber,
    profileImageUrl,
    isPhoneNumberVerified = false,
    badge,
    isSwingHero = false,
  } = props;

  const parsedPhoneNumber = phoneNumber ? phoneNumberParser({ phoneNumber }) : "";

  const maybeFormattedPhoneNumber = phoneNumber
    ? parsedPhoneNumber.length > 10
      ? phoneNumber
      : phoneNumberFormatter({
          phoneNumber: parsedPhoneNumber,
        }).value
    : undefined;

  const SwingHeroBanner = () => (
    <div className={styles.swingHeroBanner}>
      <span>{translate("SUB_PROFILE_CONTACT_SWING")}</span>
      <Icon name="Swing Hero" />
      <span>{translate("SUB_PROFILE_CONTACT_HERO")}</span>
    </div>
  );

  const ContactDetails = () => (
    <div className={styles.contactInfoContent}>
      {name && (
        <div className={styles.contactName}>
          <span>{name}</span>
        </div>
      )}
      {phoneNumber && (
        <div className={styles["flex-row-center"]}>
          <div style={badge ? { alignSelf: "flex-start" } : {}}>
            <Icon name="Phone" />
          </div>
          <div>
            <span style={{ color: Colors.black }}>{maybeFormattedPhoneNumber}</span>
            {!isPhoneNumberVerified && maybeFormattedPhoneNumber && badge && (
              <div style={{ marginTop: "4px" }}>{badge}</div>
            )}
          </div>
        </div>
      )}
      {email && (
        <div className={styles["flex-row-center"]}>
          <Icon name="Mail" />
          <span className={styles.email}>{email}</span>
        </div>
      )}
    </div>
  );

  return (
    <Section title="" onEditClick={editable?.isEditable ? editable.onEditPhoneNumber : undefined}>
      <div
        className={styles.contactInfo}
        // Use -24px margin to compensate for space when section has no title
        style={{ marginTop: editable?.isEditable ? "-24px" : 0 }}
      >
        <div style={{ width: 92, position: "relative" }}>
          {profileImageUrl ? (
            isSwingHero ? (
              <div className={styles.swingHeroWrapper}>
                <div className={styles.profileImgWrapper}>
                  <img src={profileImageUrl} alt={name} className={styles.profileImg} />
                </div>
                <SwingHeroBanner />
              </div>
            ) : (
              <div className={styles.profileImgWrapper}>
                <img src={profileImageUrl} alt={name} className={styles.profileImg} />
              </div>
            )
          ) : isSwingHero ? (
            <div className={styles.swingHeroWrapper}>
              <div className={styles.profileImgWrapper}>
                <img
                  src={subPhotoPlaceholderPNG}
                  alt={translate("SUB_PROFILE_CONTACT_IMAGE_ALT")}
                  style={{ width: 92 }}
                />
              </div>
              <SwingHeroBanner />
            </div>
          ) : (
            <img
              src={subPhotoPlaceholderPNG}
              alt={translate("SUB_PROFILE_CONTACT_IMAGE_ALT")}
              style={{ width: 92 }}
            />
          )}
          {editable?.isEditable && (
            <div
              className={styles.editButtonWrapper}
              style={
                isSwingHero
                  ? {
                      bottom: "10px",
                      right: "-14px",
                    }
                  : {}
              }
            >
              <IonButton
                className={styles.editButton}
                size="small"
                shape="round"
                onClick={editable.onEditAvatar}
              >
                <div className={styles.editIconWrapper}>
                  <Icon name="Edit" color={Colors.white200} />
                </div>
              </IonButton>
            </div>
          )}
        </div>
        {(name || email || phoneNumber) && <ContactDetails />}
      </div>
      {!profileImageUrl && (
        <div style={{ marginTop: isSwingHero ? "20px" : "8px" }}>
          <Alert color="warning">{translate("SUB_PROFILE_NO_IMAGE_TEXT")}</Alert>
        </div>
      )}
    </Section>
  );
}
